import html2canvas from "html2canvas";
import generatePDF from "react-to-pdf";

const CLOUTFLOW_LOGO_PATH = "/cloutflow.svg";

async function createLogoBannerDataUrl(
  logoSrc: string
): Promise<{ dataUrl: string; aspect: number }> {
  const banner = document.createElement("div");
  banner.style.position = "fixed";
  banner.style.top = "0";
  banner.style.left = "0";
  banner.style.zIndex = "-1";
  banner.style.pointerEvents = "none";
  banner.style.background = "#ffffff";
  banner.style.display = "inline-flex";
  banner.style.alignItems = "center";
  banner.style.gap = "6px";
  banner.style.fontFamily = "Inter, Arial, sans-serif";
  banner.style.fontSize = "16px";
  banner.style.fontWeight = "600";
  banner.style.color = "#1a1f36";
  banner.style.padding = "2px 4px";

  banner.innerHTML = `
    <span style="overflow: hidden; display: inline-block;">
      <img alt="Cloutflow" src="${logoSrc}" style="display: block; object-fit: contain; height: 32px;" />
    </span>
    <span>Cloutflow</span>
  `;

  document.body.appendChild(banner);

  try {
    const img = banner.querySelector("img");
    if (img && !img.complete) {
      await new Promise<void>((resolve) => {
        img.onload = () => resolve();
        img.onerror = () => resolve();
      });
    }

    const canvas = await html2canvas(banner, {
      useCORS: true,
      scale: 2,
      backgroundColor: "#ffffff",
      logging: false,
    });
    return {
      dataUrl: canvas.toDataURL("image/png"),
      aspect: canvas.width / canvas.height,
    };
  } finally {
    if (banner.parentNode) {
      banner.parentNode.removeChild(banner);
    }
  }
}

export async function downloadScriptPdf({
  username,
  markdown,
}: {
  username?: string;
  markdown: string;
}): Promise<void> {
  const content = markdown.trim();
  if (!content) return;

  const handle = username ? username.replace(/^@+/, "") : "creator";
  const filename = `${handle}-script.pdf`;

  const editorTarget = document.querySelector<HTMLElement>(".bn-editor");
  const bodyHtml = editorTarget
    ? editorTarget.innerHTML
    : content.startsWith("<")
      ? content
      : `<div style="white-space: pre-wrap;">${content}</div>`;

  const { dataUrl, aspect } = await createLogoBannerDataUrl(CLOUTFLOW_LOGO_PATH);

  const container = document.createElement("div");
  container.id = "cloutflow-script-pdf-render-target";
  container.style.position = "fixed";
  container.style.top = "0";
  container.style.left = "0";
  container.style.width = "720px";
  container.style.backgroundColor = "#ffffff";
  container.style.padding = "10px 40px";
  container.style.boxSizing = "border-box";
  container.style.fontFamily = "Inter, Arial, sans-serif";
  container.style.zIndex = "-1";
  container.style.pointerEvents = "none";

  container.innerHTML = `
    <div class="bn-editor bn-default-styles" style="font-family: inherit;">
      ${bodyHtml}
    </div>
  `;

  document.body.appendChild(container);

  try {
    const images = Array.from(container.querySelectorAll("img"));
    await Promise.all(
      images.map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) {
              resolve();
            } else {
              img.onload = () => resolve();
              img.onerror = () => resolve();
            }
          })
      )
    );

    await new Promise<void>((resolve) => {
      window.requestAnimationFrame(() => {
        window.setTimeout(() => resolve(), 80);
      });
    });

    const pdf = await generatePDF(() => container, {
      method: "build",
      filename,
      page: {
        margin: {
          top: 22,
          bottom: 22,
          left: 15,
          right: 15,
        },
        format: "A4",
        orientation: "portrait",
      },
      canvas: {
        mimeType: "image/jpeg",
        qualityRatio: 1,
        useCORS: true,
        logging: false,
      },
    });

    if (pdf) {
      const totalPages =
        typeof pdf.getNumberOfPages === "function"
          ? pdf.getNumberOfPages()
          : pdf.internal.pages.length - 1;

      const leftMarginMM = 15;
      const topY = 6;
      const pageHeightMM = pdf.internal.pageSize.getHeight();
      const bottomY = pageHeightMM - 15;
      const bannerHeightMM = 9;
      const bannerWidthMM = bannerHeightMM * aspect;

      for (let i = 1; i <= totalPages; i += 1) {
        pdf.setPage(i);
        pdf.addImage(dataUrl, "PNG", leftMarginMM, topY, bannerWidthMM, bannerHeightMM);
        pdf.addImage(dataUrl, "PNG", leftMarginMM, bottomY, bannerWidthMM, bannerHeightMM);
      }

      await pdf.save(filename, { returnPromise: true });
    }
  } finally {
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  }
}
